import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProducts extends HTMLElement {
  #loading = false;

  #mutationObserver = new MutationObserver((mutations) => {
    if (!Shopify.designMode) return;

    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (
        mutation.attributeName === 'data-recently-viewed-performed' &&
        this.dataset.recentlyViewedPerformed === 'true'
      ) {
        continue;
      }

      this.#loadProducts();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedSections = {};

  connectedCallback() {
    if (this.dataset.recentlyViewedPerformed === 'true') return;

    const viewedProductIds = this.#getViewedProductIds();

    if (viewedProductIds.length === 0) {
      this.#hideSection();
      return;
    }

    if (Shopify.designMode) {
      this.#mutationObserver.observe(this, { attributes: true });
    }

    this.#loadProducts();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  #getViewedProductIds() {
    const { maxProducts, excludeCurrentProduct, currentProductId } = this.dataset;
    let viewedProductIds = RecentlyViewed.getProducts().map(String);

    if (excludeCurrentProduct === 'true' && currentProductId) {
      viewedProductIds = viewedProductIds.filter(
        (productId) => String(productId) !== String(currentProductId)
      );
    }

    const limit = Number.parseInt(maxProducts || '4', 10);
    return viewedProductIds.slice(0, limit);
  }

  #loadProducts() {
    const { sectionId } = this.dataset;
    const id = this.id;

    if (!sectionId || !id || this.#loading) return;

    if (this.dataset.recentlyViewedPerformed === 'true') {
      return;
    }

    const viewedProductIds = this.#getViewedProductIds();

    if (viewedProductIds.length === 0) {
      this.#hideSection();
      return;
    }

    this.#loading = true;

    this.#fetchSectionHTML(sectionId, viewedProductIds)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#hideSection();
          }
          return;
        }

        const parsed = new DOMParser().parseFromString(result.data || '', 'text/html');
        const component =
          parsed.querySelector(`recently-viewed-products[id="${id}"]`) ||
          parsed.querySelector('recently-viewed-products');

        if (!component?.innerHTML?.trim()) {
          if (!Shopify.designMode) {
            this.#hideSection();
          }
          return;
        }

        const hasProducts = component.querySelector('[data-has-products="true"]');

        if (!hasProducts && !Shopify.designMode) {
          this.#hideSection();
          return;
        }

        this.#mutationObserver.disconnect();
        this.dataset.recentlyViewedPerformed = 'true';
        this.dataset.hasProducts = 'true';
        this.classList.remove('hidden');
        this.innerHTML = component.innerHTML;
      })
      .catch(() => {
        if (!Shopify.designMode) {
          this.#hideSection();
        }
      })
      .finally(() => {
        this.#loading = false;
      });
  }

  /**
   * @param {string} sectionId
   * @param {string[]} productIds
   */
  async #fetchSectionHTML(sectionId, productIds) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    const cacheKey = `${sectionId}:${productIds.join(',')}`;
    const cachedResponse = this.#cachedSections[cacheKey];

    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    try {
      const text = await sectionRenderer.getSectionHTML(sectionId, false, url);
      this.#cachedSections[cacheKey] = text;
      return { success: true, data: text };
    } catch {
      return { success: false, status: 0 };
    }
  }

  #hideSection() {
    this.#mutationObserver.disconnect();
    this.dataset.recentlyViewedPerformed = 'true';
    this.dataset.hasProducts = 'false';
    this.classList.add('hidden');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
